import moment from 'moment';

import { parsePhoneNumber } from 'utils/parsePhoneNumber';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { VAT_NUMBER_KEY } from 'constants/clubEurostar';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMER_DETAILS } from 'store/queries';
import { useMemo } from 'react';

export const areThalysProductsConformWithRequest = (products, date) =>
  !!products && !!products.length && products.some((p) => p.validityDate === moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD'));

export const buildThalysPassRequest = (formValues, membershipNumber) => {
  const countryCodeAndPhoneNumber = `${formValues.passengerPhoneCountryCode.padStart(3, '0')}${formValues.passengerPhoneNumber}`;

  const passenger = {
    id: 'passenger_1',
    type: 'AD',
    firstName: formValues.passengerFirstName,
    lastName: formValues.passengerLastName,
    email: formValues.passengerEmail,
    phoneNumber: countryCodeAndPhoneNumber,
    birthDate: formValues.passengerDateOfBirth,
    discountCards: [
      {
        code: productFamilyCodes.EUROSTAR_LOYALTY,
        identifier: membershipNumber,
        name: 'Club Eurostar Membership Number',
        requiresPassengerIdentifier: false
      }
    ]
  };

  const customer = {
    firstName: formValues.passengerFirstName,
    lastName: formValues.passengerLastName,
    email: formValues.passengerEmail,
    phoneNumber: countryCodeAndPhoneNumber,
    companyName: formValues.invoicingCompany || formValues.passengerCompany,
    address: {
      street: formValues.invoicingStreet || formValues.passengerStreet,
      postalCode: formValues.invoicingPostcode || formValues.passengerPostcode,
      city: formValues.invoicingCity || formValues.passengerCity,
      countryCode: formValues.invoicingCountry || formValues.passengerCountry
    }
  };

  const additionalDetails = [
    {
      key: VAT_NUMBER_KEY,
      value: formValues.vatValidNumber
        ? formValues.vatValidNumber
        : formValues.vatCountry && formValues.vatNumber
        ? `${formValues.vatCountry}${formValues.vatNumber}`
        : 'N/A'
    }
  ];

  return {
    language: 'EN', // Hardcoded to English for now https://eurostar.atlassian.net/browse/VOY-1366
    currency: 'EUR',
    segments: [
      {
        startValidityDate: moment(formValues.validityDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        items: [
          {
            passengerId: 'passenger_1',
            tariffCode: formValues.thalysProduct
          }
        ]
      }
    ],
    passengers: [passenger],
    customer,
    additionalDetails
  };
};

export const useCustomerDetails = (membershipNumber) => {
  const { loading, data } = useQuery(GET_CUSTOMER_DETAILS, {
    variables: {
      membershipNumber
    }
  });

  const customerDetials = useMemo(() => {
    const rawPhoneNumber = data?.getCustomerDetails?.phoneNumber;
    const parsedPhone = rawPhoneNumber ? parsePhoneNumber(rawPhoneNumber) : null;

    return {
      passengerFirstName: data?.getCustomerDetails?.firstName || '',
      passengerLastName: data?.getCustomerDetails?.lastName || '',
      passengerEmail: data?.getCustomerDetails?.emailAddress || '',
      passengerDateOfBirth: data?.getCustomerDetails?.profile?.dateOfBirth || '',
      passengerStreet: data?.getCustomerDetails?.address?.street || '',
      passengerCity: data?.getCustomerDetails?.address?.city || '',
      passengerPostcode: data?.getCustomerDetails?.address?.postalCode || '',
      passengerCountry: data?.getCustomerDetails?.address?.countryCode || '',
      passengerPhoneNumber: parsedPhone?.number || '',
      passengerPhoneCountryCode: parsedPhone?.countryCode.replace('+', '') || ''
    };
  }, [data]);

  return {
    loading: loading,
    data: customerDetials
  };
};
